import React, { Component } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Tabs, Button, Popconfirm, Row, Col, Typography } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faHashtag,
  faFileSignature,
} from "@fortawesome/pro-light-svg-icons";
import {
  PhoneOutlined,
  MinusCircleOutlined,
  MailOutlined,
  CheckCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { uniq, isEmpty } from "lodash";
import {
  fetchPartyById,
  updateParty,
  deleteParty,
} from "@mds/common/redux/slices/partiesSlice";
import { fetchMineBasicInfoList } from "@mds/common/redux/actionCreators/mineActionCreator";
import { openModal, closeModal } from "@mds/common/redux/actions/modalActions";
import { getParties } from "@mds/common/redux/slices/partiesSlice";
import { getMineBasicInfoListHash } from "@mds/common/redux/selectors/mineSelectors";
import {
  getDropdownProvinceOptions,
  getPartyRelationshipTypeHash,
  getPartyBusinessRoleOptionsHash,
} from "@mds/common/redux/selectors/staticContentSelectors";
import { deletePartyWalletConnection } from "@mds/common/redux/actionCreators/verifiableCredentialActionCreator";
import { formatDate, dateSorter } from "@common/utils/helpers";
import * as Strings from "@mds/common/constants/strings";
import { EDIT } from "@/constants/assets";
import { modalConfig } from "@/components/modalContent/config";
import Loading from "@/components/common/Loading";
import * as routes from "@/constants/routes";
import * as ModalContent from "@/constants/modalContent";
import * as Permission from "@/constants/permissions";
import AuthorizationWrapper from "@/components/common/wrappers/AuthorizationWrapper";
import CustomPropTypes from "@/customPropTypes";
import Address from "@/components/common/Address";
import CoreTable from "@mds/common/components/common/CoreTable";
import { VC_CONNECTION_STATES } from "@mds/common/constants/enums";

/**
 * @class PartyProfile - profile view for personnel/companies
 */

const propTypes = {
  fetchPartyById: PropTypes.func.isRequired,
  fetchMineBasicInfoList: PropTypes.func.isRequired,
  history: PropTypes.shape({ push: PropTypes.func }).isRequired,
  updateParty: PropTypes.func.isRequired,
  deleteParty: PropTypes.func.isRequired,
  deletePartyWalletConnection: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  parties: PropTypes.arrayOf(CustomPropTypes.party).isRequired,
  partyRelationshipTypeHash: PropTypes.objectOf(PropTypes.strings),
  partyBusinessRoleOptionsHash: PropTypes.objectOf(PropTypes.strings),
  mineBasicInfoListHash: PropTypes.objectOf(PropTypes.strings),
  match: CustomPropTypes.match.isRequired,
  provinceOptions: PropTypes.arrayOf(CustomPropTypes.dropdownListItem).isRequired,
};

const defaultProps = {
  partyRelationshipTypeHash: {},
  mineBasicInfoListHash: {},
  partyBusinessRoleOptionsHash: {},
};

export class PartyProfile extends Component {
  state = { isLoaded: false, deletingParty: false };

  componentDidMount() {
    const { id } = this.props.match.params;
    this.props.fetchPartyById(id).then(() => {
      const mine_guids = uniq(
        this.props.parties[id].mine_party_appt
          .filter((x) => x.mine_guid !== "None")
          .map(({ mine_guid }) => mine_guid)
      );
      this.props.fetchMineBasicInfoList(mine_guids).then(() => {
        this.setState({ isLoaded: true });
      });
    });
  }

  componentWillReceiveProps = (nextProps) => {
    if (nextProps.match !== this.props.match) {
      const { id } = nextProps.match.params;
      this.props.fetchPartyById(id);
    }
  };

  openEditPartyModal = (event, partyGuid, onSubmit, title) => {
    event.preventDefault();
    this.props.openModal({
      props: { partyGuid, onSubmit, title },
      content: modalConfig.EDIT_PARTY,
      width: "75vw",
      clearOnSubmit: false,
    });
  };

  editParty = (values) => {
    const { id } = this.props.match.params;
    return this.props.updateParty({ data: values, partyGuid: id }).then(() => {
      this.props.fetchPartyById(id);
      this.props.closeModal();
    });
  };

  deleteParty = () => {
    const { id } = this.props.match.params;
    this.setState({ deletingParty: true });
    this.props
      .deleteParty(id)
      .then(() => {
        this.props.history.push(
          routes.CONTACT_HOME_PAGE.dynamicRoute({
            page: String.DEFAULT_PAGE,
            per_page: String.DEFAULT_PER_PAGE,
          })
        );
      })
      .finally(() => this.setState({ deletingParty: false }));
  };

  deletePartyWalletConnection = () => {
    const { id } = this.props.match.params;
    this.setState({ deletingParty: true });
    this.props
      .deletePartyWalletConnection(id)
      .then(this.props.fetchPartyById(id))
      .finally(() => this.setState({ deletingParty: false }));
  };

  render() {
    const { id } = this.props.match.params;
    const party = this.props.parties[id];
    const columns = [
      {
        title: "Name",
        dataIndex: "mineName",
        render: (text, record) => {
          if (record.relationship.mine_party_appt_type_code === "PMT") {
            return <div title="Permit No">{record.relationship.permit_no}</div>;
          }
          if (record.relationship.party_business_role_code === "INS") {
            return "N/A";
          }
          if (record.relationship.party_business_role_code === "PRL") {
            return "N/A";
          }
          if (record.relationship.party_business_role_code === "CNA") {
            return "N/A";
          }
          if (record.relationship.mine_party_appt_type_code === "AGT") {
            return (
              <div title="NoW Number">
                <Link
                  to={routes.NOTICE_OF_WORK_APPLICATION.dynamicRoute(
                    record.relationship.now_application.now_application_guid,
                    "application"
                  )}
                >
                  {record.relationship.now_application.now_number}
                </Link>
              </div>
            );
          }
          return (
            <div title="Mine Name">
              <Link to={routes.MINE_CONTACTS.dynamicRoute(record.mineGuid)}>{text}</Link>
            </div>
          );
        },
      },
      {
        title: "Role",
        dataIndex: "role",
        render: (text) => <div title="Role">{text}</div>,
      },
      {
        title: "Dates",
        dataIndex: "dates",
        sorter: dateSorter("startDate"),
        defaultSortOrder: "descend",
        render: (text, record) => (
          <div title="Dates">
            {record.startDate} - {record.endDate}
          </div>
        ),
      },
    ];

    const transformRowData = (partyRelationships) =>
      partyRelationships.map((relationship) => ({
        key: relationship.mine_party_appt_guid,
        mineGuid: relationship.mine_guid,
        mineName: this.props.mineBasicInfoListHash[relationship.mine_guid],
        role: this.props.partyRelationshipTypeHash[relationship.mine_party_appt_type_code],
        endDate: formatDate(relationship.end_date) || "Present",
        startDate: formatDate(relationship.start_date) || "Unknown",
        relationship,
      }));

    const transformBusinessRoleRowData = (businessPartyRecord) =>
      businessPartyRecord.map((record) => ({
        key: record.party_business_role_appt_id,
        role: this.props.partyBusinessRoleOptionsHash[record.party_business_role_code],
        endDate: formatDate(record.end_date) || "Present",
        startDate: formatDate(record.start_date) || "Unknown",
        relationship: { party_business_role_code: record.party_business_role_code },
      }));

    const transformNOWRoleRowData = (NOWPartyRecords) => {
      return NOWPartyRecords.map((record) => ({
        key: record.now_party_appointment_id,
        role: this.props.partyRelationshipTypeHash[record.mine_party_appt_type_code],
        endDate: formatDate(record.end_date) || "Present",
        startDate: formatDate(record.now_application.submitted_date) || "Unknown",
        relationship: record,
      }));
    };

    if (this.state.isLoaded && party) {
      return (
        <div className="profile">
          <div className="profile__header">
            <Typography.Title level={1}>Contact Details</Typography.Title>
            <Row>
              <Col md={8} xs={12}>
                <Typography.Title level={2}>{party.name}</Typography.Title>
              </Col>
              <Col md={8} xs={12}></Col>
              <Col md={8} xs={12} style={{ display: "flex", justifyContent: "flex-end" }}>
                <AuthorizationWrapper permission={Permission.EDIT_PARTIES}>
                  <Button
                    type="primary"
                    onClick={(event) =>
                      this.openEditPartyModal(
                        event,
                        party.party_guid,
                        this.editParty,
                        ModalContent.EDIT_PARTY(party.name)
                      )
                    }
                    disabled={this.state.deletingParty}
                  >
                    <img alt="pencil" className="padding-sm--right" src={EDIT} />
                    Update Contact
                  </Button>
                </AuthorizationWrapper>
                <AuthorizationWrapper permission={Permission.ADMIN}>
                  <Popconfirm
                    className="delete_contact_warning"
                    placement="bottom"
                    title={
                      <div>
                        <p>Are you sure you want to delete the party &apos;{party.name}&apos;?</p>
                        <p>Doing so will permanently remove the party and all associated roles.</p>
                      </div>
                    }
                    onConfirm={this.deleteParty}
                    okText="Yes"
                    cancelText="No"
                    disabled={this.state.deletingParty}
                  >
                    <Button type="danger" disabled={this.state.deletingParty}>
                      <MinusCircleOutlined className="btn-danger--icon" />
                      Delete Party
                    </Button>
                  </Popconfirm>
                </AuthorizationWrapper>
              </Col>
            </Row>

            <Row>
              <Col md={8} xs={12}>
                <strong>Contact Details</strong>
              </Col>
              <Col md={8} xs={12}>
                <strong>Address</strong>
              </Col>
            </Row>
            <Row>
              <Col md={8} xs={12}>
                <div className="inline-flex">
                  <div className="padding-right">
                    <MailOutlined className="icon-sm" />
                  </div>
                  {party.email && party.email !== "Unknown" ? (
                    <a href={`mailto:${party.email}`}>{party.email}</a>
                  ) : (
                    <p>{Strings.EMPTY_FIELD}</p>
                  )}
                </div>
                <div className="inline-flex">
                  <div className="padding-right">
                    <PhoneOutlined className="icon-sm" />
                  </div>
                  <p>
                    {party.phone_no} {party.phone_ext ? `x${party.phone_ext}` : ""}
                  </p>
                </div>
              </Col>
              <Col md={8} xs={12}>
                <div className="inline-flex">
                  <div className="padding-right">
                    <Address address={party.address[0] || {}} />
                  </div>
                </div>
              </Col>
              <Col md={8} xs={12}>
                {!isEmpty(party.party_bc_registration) && (
                  <div className="light-grey-background padding-md">
                    <Typography.Title level={4}>
                      <CheckCircleOutlined style={{ paddingRight: 5 }} />
                      OrgBook Registration Information{" "}
                      <a
                        href={routes.ORGBOOK_ENTITY_URL(party.party_bc_registration.registration_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                      </a>
                    </Typography.Title>
                    <br />
                    <FontAwesomeIcon className="fa-fw" icon={faHashtag} />
                    <span className="padding-left">
                      {party.party_bc_registration.registration_id}
                    </span>
                    <br />
                    <FontAwesomeIcon className="fa-fw" icon={faFileSignature} />
                    <span className="padding-left">{party.party_bc_registration.name_text}</span>
                  </div>
                )}
              </Col>
            </Row>
            <div className="inline-flex">
              <div className="padding-right">
                <EditOutlined className="icon-sm" />
              </div>
              {party.signature ? (
                <img
                  src={party.signature}
                  alt="Signature"
                  style={{ pointerEvents: "none", userSelect: "none" }}
                  height={120}
                />
              ) : (
                <p>No Signature Provided</p>
              )}
            </div>

            <div className="padding-md--top">
              Digital Wallet Connection Status:{" "}
              {VC_CONNECTION_STATES[party?.digital_wallet_connection_status]}
              {VC_CONNECTION_STATES[party?.digital_wallet_connection_status] === "Active" && (
                <Popconfirm
                  title={
                    <div>
                      <p>
                        Are you sure you want to delete the digitial wallet connection for &apos;
                        {party.name}&apos;? This is irreversable and destructive.
                      </p>
                    </div>
                  }
                  onConfirm={this.deletePartyWalletConnection}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="danger">
                    <img alt="pencil" className="padding-sm--right" src={EDIT} />
                    Delete Wallet Connection
                  </Button>
                </Popconfirm>
              )}
            </div>
          </div>
          <div className="profile__content">
            <Tabs
              activeKey="history"
              size="large"
              animated={{ inkBar: true, tabPane: false }}
              centered
            >
              <Tabs.TabPane tab="History" key="history">
                <div className="tab__content ">
                  <CoreTable
                    columns={columns}
                    dataSource={transformRowData(this.props.parties[id].mine_party_appt).concat(
                      transformBusinessRoleRowData(
                        this.props.parties[id].business_role_appts
                      ).concat(transformNOWRoleRowData(this.props.parties[id].now_party_appt))
                    )}
                  />
                </div>
              </Tabs.TabPane>
            </Tabs>
          </div>
        </div>
      );
    }
    return <Loading />;
  }
}

const mapStateToProps = (state) => ({
  parties: getParties(state),
  partyRelationshipTypeHash: getPartyRelationshipTypeHash(state),
  partyBusinessRoleOptionsHash: getPartyBusinessRoleOptionsHash(state),
  mineBasicInfoListHash: getMineBasicInfoListHash(state),
  provinceOptions: getDropdownProvinceOptions(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchPartyById,
      fetchMineBasicInfoList,
      deleteParty,
      deletePartyWalletConnection,
      updateParty,
      openModal,
      closeModal,
    },
    dispatch
  );

PartyProfile.propTypes = propTypes;
PartyProfile.defaultProps = defaultProps;

export default connect(mapStateToProps, mapDispatchToProps)(PartyProfile);
